<script lang="ts">
export type InferValue<T, M extends boolean> = M extends true ? T[] : T | null;

export interface ComboboxProps<
	TOption,
	TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
	TValue extends ResolveValue<TOption, TValueKey>,
	TMultiple extends boolean,
> extends ClassProps,
		InputVariantProps,
		Omit<ComboboxRootProps<TValue>, "modelValue" | "ignoreFilter">,
		ListProps<TOption, TValueKey>,
		VirtualizerProps {
	modelValue?: InferValue<TValue, TMultiple>;
	multiple?: TMultiple & boolean;
	inline?: boolean;
	placeholder?: string;
	customItem?: boolean;
	clearable?: boolean;
	/* @vue-ignore*/ "v-slots"?: Partial<ComboboxSlots<TOption, TValue>>;
}

export interface ComboboxEmit<TOption, TValue, TMultiple extends boolean>
	extends Omit<ComboboxRootEmits<TValue>, "update:modelValue">,
		ComboboxListOptionsEmit<TOption, TValue> {
	"update:modelValue": [value: InferValue<TValue, TMultiple>];
	"keyup:enter": [event: KeyboardEvent];
	clear: [];
	blur: [];
	select: [evt: { option: TOption; value: TValue }];
}

export type ComboboxSlots<TOption, TValue> = {
	selection?(slotProps: {
		option: TOption;
		value: TValue;
		open: boolean;
		deselect: () => void;
	}): void;
	prepend?(): void;
	append?(): void;
	option?(slotProps: { option: TOption }): void;
	groupLabel?(slotProps: { label: string }): void;
	default?(slotProps: { filter: string | undefined }): void;
	customItem?(slotProps: { value: string }): void;
};

export interface ComboboxContext {
	items: Map<AcceptableValue, AcceptableValue>;
}

export const [injectComboboxContext, provideComboboxContext] =
	createContext<ComboboxContext>("UICombobox");
</script>

<script
	setup
	lang="ts"
	generic="
		TOption,
		TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
		TValue extends ResolveValue<TOption, TValueKey>,
		TMultiple extends boolean = false
	"
>
import { mdiClose, mdiUnfoldMoreHorizontal } from "@mdi/js";
import {
	ComboboxAnchor,
	ComboboxPortal,
	ComboboxRoot,
	ComboboxTrigger,
	createContext,
	useForwardPropsEmits,
	type AcceptableValue,
	type ComboboxRootEmits,
	type ComboboxRootProps,
} from "reka-ui";
import { computed, useTemplateRef } from "vue";
import { cn } from "../../lib/utils.ts";
import Button from "../button/Button.vue";
import Icon from "../icon/Icon.vue";
import { inputCls, type InputVariantProps } from "../input/Input.vue";
import {
	useListOptions,
	type KeysWithType,
	type ListOptionAcceptableValue,
	type ListProps,
	type OptionCustomItem,
	type OptionItem,
	type ResolveValue,
} from "../listbox/listbox.ts";
import type { VirtualizerProps } from "../virtualizer/virtualizer.ts";
import { useDelegatedProps, type ClassProps } from "./../../lib/base-props.ts";
import ComboboxContent from "./ComboboxContent.vue";
import ComboboxInput from "./ComboboxInput.vue";
import ComboboxListOptions, { type ComboboxListOptionsEmit } from "./ComboboxListOptions.vue";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<ComboboxProps<TOption, TValueKey, TValue, TMultiple>>(), {
	resetSearchTermOnBlur: true,
	resetSearchTermOnSelect: true,
	openOnClick: true,
	openOnFocus: true,
});
const emit = defineEmits<ComboboxEmit<TOption, TValue, TMultiple>>();
defineSlots<ComboboxSlots<TOption, TValue>>();

const filter = defineModel<string>("filter", { default: "" });

const delegatedProps = useDelegatedProps(props);

const forwarded = useForwardPropsEmits(delegatedProps, emit);

function deselect(value: TValue) {
	let newValue = props.modelValue as any;
	if (Array.isArray(newValue)) {
		newValue = newValue.filter((v) => v !== value);
	} else {
		newValue = undefined;
	}

	emit("update:modelValue", newValue as any);
	emit("blur");
}

function onOpenChange(open: boolean) {
	if (!open) {
		emit("blur");
	}
}

const { options, filteredOptions } = useListOptions(props, filter);

const optionsMap = computed(() => {
	return new Map(options.value.filter((o) => o.type === "item").map((o) => [o.value, o]));
});

function resolveOption(value: TValue): OptionItem<TOption, TValue> | OptionCustomItem<TValue> {
	const option = optionsMap.value.get(value);
	if (option == null) {
		return {
			type: "custom-item",
			value: value,
		};
	}

	return option as any;
}

const allSelections = computed(() => {
	if (props.modelValue == null || props.modelValue === "") {
		return [];
	}

	if (Array.isArray(props.modelValue)) {
		return props.modelValue.map(resolveOption);
	}

	return [resolveOption(props.modelValue as any)];
});

const comboboxRootEl = useTemplateRef("$comboboxRoot");
const inputEl = useTemplateRef("$comboboxInput");
defineExpose({
	root: comboboxRootEl,
	focus() {
		inputEl.value?.$el.focus();
	},
});
</script>

<template>
	<ComboboxRoot
		ref="$comboboxRoot"
		v-slot="{ open }"
		ignoreFilter
		v-bind="forwarded"
		@update:open="onOpenChange"
	>
		<ComboboxAnchor
			:class="cn(inputCls(delegatedProps), 'h-[unset] min-h-[var(--size)] w-full', props.class)"
		>
			<slot name="prepend" />

			<div class="flex w-1 flex-auto shrink grow flex-wrap gap-2 py-2">
				<template v-for="selection of allSelections" :key="selection">
					<slot
						name="selection"
						:option="selection.type === 'item' ? selection.option : (selection.value as any)"
						:value="selection.value"
						:open="open"
						:deselect="() => deselect(selection.value)"
					>
						<div class="text-wrap">
							{{ selection.type === "item" ? selection.label : selection.value }}
						</div>
					</slot>
				</template>

				<ComboboxInput
					ref="$comboboxInput"
					v-model="filter"
					class="w-4 min-w-4 grow"
					:placeholder="allSelections.length === 0 ? props.placeholder : ''"
					v-bind="$attrs"
					@keyup.enter="(evt) => emit('keyup:enter', evt)"
					@select.stop
				/>
			</div>

			<Button
				v-if="props.clearable && props.modelValue != null"
				size="sm"
				shape="square"
				variant="ghost"
				@click="
					() => {
						emit('update:modelValue', (props.multiple ? [] : null) as any);
						emit('clear');
					}
				"
			>
				<Icon :d="mdiClose" />
			</Button>

			<slot name="append">
				<ComboboxTrigger asChild>
					<div class="size-4 flex-initial cursor-pointer">
						<Icon size="small" :d="mdiUnfoldMoreHorizontal" />
					</div>
				</ComboboxTrigger>
			</slot>
		</ComboboxAnchor>

		<ComboboxPortal :disabled="props.inline">
			<ComboboxContent
				class="popover max-h-75 popover-animate border p-0"
				:sideOffset="4"
				position="popper"
				side="bottom"
				align="center"
				positionStrategy="fixed"
				prioritizePosition
				updatePositionStrategy="optimized"
			>
				<ComboboxListOptions
					:filter="filter"
					:options="filteredOptions"
					:virtual="props.virtual"
					:overscan="props.overscan"
					:estimateHeight="props.estimateHeight"
					:customItem="props.customItem"
					@select="
						(evt) => {
							emit('select', evt as any);
						}
					"
				>
					<template #customItem="slotProps">
						<slot name="customItem" v-bind="slotProps" />
					</template>

					<template #option="slotProps">
						<slot name="option" v-bind="slotProps" />
					</template>

					<template #label="slotProps">
						<slot name="groupLabel" v-bind="slotProps" />
					</template>
				</ComboboxListOptions>

				<slot :filter="filter" />
			</ComboboxContent>
		</ComboboxPortal>
	</ComboboxRoot>
</template>
